import React, { useEffect } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import styled from 'styled-components';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  TablePagination,
  IconButton,
  Collapse,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

import { translate } from 'containers/services/translate';
import messages from 'containers/ManagerDocumentsPage/messages';
import { BUTTON_COLORS, COLORS } from 'constants/design';
import { ROW_PER_PAGE } from 'constants/global';
import { uniqueId, isEmpty } from 'lodash';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import {
  DOCUMENT_STATUS,
  DOCUMENT_DECISION_VALUES,
  DOCUMENT_URL_STATUS,
} from 'containers/ManagerDocumentsPage/constants';
import {
  Document,
  ExistingClaim,
} from 'containers/ManagerDocumentsPage/typings';
import moment from 'moment-timezone';
import { openDocument } from 'containers/UploadDocuments/utils';
import PrimaryButton from 'components/Buttons/PrimaryButton';

const Container = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  .titleContainer {
    display: flex;
    align-items: center;
  }
  .documentsCount {
    padding: 5px;
    background: transparent
      linear-gradient(229deg, #707891 0%, #0b7cc5 0%, #56afbb 88%, #5db4ba 100%)
      0% 0% no-repeat padding-box;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: ${COLORS.WHITE};
    font-weight: bold;
  }
  .title {
    padding: 5px;
    margin-left: 12px;
    font-family: Roboto;
    color: #384264;
    font-size: 18px;
    font-weight: bold;
  }
  .collapse-cell {
    border-bottom: unset;
    padding-top: 0;
    padding-bottom: 0;
  }
  .documents-container {
    width: 100%;
    padding: 10px;
    display: flex;
    flex-direction: column;
  }
  .required-document-title {
    font: 14px;
    color: #0075c9;
    font-weight: bold;
  }
  .save-button {
    width: 114px;
    height: 36px;
    background-color: #1175c9;
    border: 1px solid #0072c6;
    border-radius: 4px;
    text-align: center;
    font-size: 15px;
    color: #ffffff;
  }
  .docs {
    padding: 15px 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    align-items: center;
  }
  .doc {
    margin: 5px;
    padding: 15px;
    width: 327px;
    height: 55px;
    background-color: #ffffff;
    box-shadow: 0px 1px 3px #0000001f;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .doc-name {
    font-size: 14px;
    color: #2b2727;
    cursor: pointer;
  }
  .doc-title {
    display: flex;
    flex-direction: column;
  }
  .doc-type {
    font-size: 13px;
    color: #999999;
    text-transform: uppercase;
  }
  .doc-decision {
    font-size: 13px;
    color: #374065;
  }
  .checkbox .MuiFormControlLabel-label {
    font-size: 13px;
    color: #374065;
  }
  .cta {
    padding: 10px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
`;

export interface DocumentsToValidateProps {
  intl: IntlShape;
  existantClaims: ExistingClaim[];
  requestExistantClaims: Function;
  requestValidateExistingClaimDocuments: Function;
}

export const DocumentsToValidate: React.FC<DocumentsToValidateProps> = (
  props,
): JSX.Element => {
  const [open, setOpen] = React.useState({} as { [index: string]: boolean });
  const [toggle, setToggle] = React.useState<boolean>(false);

  const [page, setPage] = React.useState<number>(0);
  const [pageSize, setRowsPerPage] = React.useState<number>(25);

  const [existantClaimsDocuments, setExistantClaimsDocuments] = React.useState<
    ExistingClaim[]
  >([]);

  useEffect(() => {
    props.requestExistantClaims(
      DOCUMENT_URL_STATUS.TO_VALIDATE,
      page,
      pageSize,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setExistantClaimsDocuments(props.existantClaims);
  }, [props.existantClaims]);

  const tableHeaders = [
    messages.claimNumber,
    messages.claimNature,
    messages.policyNumber,
    messages.registrationNumber,
    messages.vehiculeBrand,
    messages.lossDate,
  ];

  const handleCollapseRow = (index: number) => () => {
    setToggle(toggle);
    setOpen({ [index]: !toggle });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    props.requestExistantClaims(
      DOCUMENT_URL_STATUS.TO_VALIDATE,
      newPage,
      pageSize,
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    props.requestExistantClaims(DOCUMENT_URL_STATUS.TO_VALIDATE, page, size);
  };

  const handleChangeRequiredDocumentsDecision = (
    claimIndex: number,
    documentIndex: number,
  ) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: string;
    }>,
  ) => {
    const existingClaims = [...props.existantClaims] || [];

    existingClaims[claimIndex].documents[documentIndex]['status'] =
      DOCUMENT_DECISION_VALUES[event.target.value as string];

    setExistantClaimsDocuments(existingClaims as ExistingClaim[]);
  };

  const handleValidateDocuments = (index: number) => () => {
    const documentsWithDecision = (existantClaimsDocuments[
      index
    ] as ExistingClaim).documents;

    props.requestValidateExistingClaimDocuments(documentsWithDecision);
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <Typography className="titleContainer">
            <span className="documentsCount">
              {existantClaimsDocuments.length}
            </span>
            <span className="title">
              {translate(messages.pendingDocumentsTitle, props)}
            </span>
          </Typography>
        </CardContent>
        <CardContent>
          <Table aria-label="collapsible table" className="claim-table">
            <TableHead>
              <TableRow className="claim-row-head">
                {tableHeaders.map(header => (
                  <TableCell key={uniqueId()}>
                    {translate(header, props)}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            {!isEmpty(existantClaimsDocuments) &&
              existantClaimsDocuments.map(
                (claim: ExistingClaim, index: number) => (
                  <TableBody className="table" key={claim.id}>
                    <TableRow>
                      <TableCell align="left">{claim.claimNumber}</TableCell>
                      <TableCell align="left">{claim.claimNature}</TableCell>
                      <TableCell align="left">{claim.policyNumber}</TableCell>
                      <TableCell align="left">
                        {claim.registrationNumber}
                      </TableCell>
                      <TableCell align="left">{claim.vehiculeBrand}</TableCell>
                      <TableCell align="left">{`${moment(claim.lossDate).format(
                        'DD/MM/YYYY',
                      )} ${claim.lossHour}`}</TableCell>
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={handleCollapseRow(index)}
                        >
                          {open[index] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        style={{
                          paddingBottom: 0,
                          paddingTop: 0,
                          backgroundColor: '#f7f7f7',
                        }}
                      >
                        <Collapse in={open[index]} timeout="auto" unmountOnExit>
                          <div className="documents-container">
                            <div className="cta">
                              <span className="required-document-title">
                                {translate(
                                  messages.requiredDocumentsTitle,
                                  props,
                                )}
                              </span>
                              <PrimaryButton
                                title={translate(
                                  messages.requiredDocumentsSaveButton,
                                  props,
                                )}
                                onClick={handleValidateDocuments(index)}
                                background={BUTTON_COLORS.PRIMARY_BLUE}
                                border={BUTTON_COLORS.PRIMARY_BLUE}
                                titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
                              />
                            </div>
                            <div className="docs">
                              {claim?.documents.map(
                                (document: Document, documentIndex: number) => (
                                  <div className="doc" key={document.id}>
                                    <div
                                      className="doc-title"
                                      onClick={openDocument(document?.url)}
                                    >
                                      <span className="doc-name">
                                        {document.name}
                                      </span>
                                      <span className="doc-type">
                                        {document.type}
                                      </span>
                                    </div>
                                    <div className="doc-decision">
                                      <FormControl>
                                        <RadioGroup
                                          row
                                          onChange={handleChangeRequiredDocumentsDecision(
                                            index,
                                            documentIndex,
                                          )}
                                          value={document.status}
                                        >
                                          <FormControlLabel
                                            className="checkbox"
                                            control={
                                              <Radio
                                                color="primary"
                                                value={
                                                  DOCUMENT_STATUS.VALIDATED
                                                }
                                                icon={
                                                  <RadioButtonUnchecked
                                                    style={{
                                                      width: '18px',
                                                      height: '18px',
                                                    }}
                                                  />
                                                }
                                                checkedIcon={
                                                  <CheckCircle
                                                    style={{
                                                      width: '18px',
                                                      height: '18px',
                                                    }}
                                                  />
                                                }
                                              />
                                            }
                                            label={translate(
                                              messages.requiredDocumentsDecisionYes,
                                              props,
                                            )}
                                          />
                                          <FormControlLabel
                                            control={
                                              <Radio
                                                color="primary"
                                                value={DOCUMENT_STATUS.REJECTED}
                                                icon={
                                                  <RadioButtonUnchecked
                                                    style={{
                                                      width: '18px',
                                                      height: '18px',
                                                    }}
                                                  />
                                                }
                                                checkedIcon={
                                                  <CheckCircle
                                                    style={{
                                                      width: '18px',
                                                      height: '18px',
                                                    }}
                                                  />
                                                }
                                              />
                                            }
                                            label={translate(
                                              messages.requiredDocumentsDecisionNo,
                                              props,
                                            )}
                                            className="checkbox"
                                          />
                                        </RadioGroup>
                                      </FormControl>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ),
              )}
          </Table>
          <TablePagination
            rowsPerPageOptions={ROW_PER_PAGE}
            component="div"
            count={props.existantClaims.length}
            rowsPerPage={pageSize}
            page={page}
            labelRowsPerPage={translate({ ...messages.linesPerPage }, props)}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default injectIntl(DocumentsToValidate);
